import { AxiosInstance } from "../../../axios/axiosInstance";
import { apiRoutes } from "../../../utils/ApiRoutes";
import { USE_CRM_IMPORT_MOCK } from "../../../components/Admin/AdminCrmImport/crmImport.config";
import {
  fixtureBusinesses,
  fixtureContext,
  fixtureCrms,
  fixtureEstimate,
  fixtureImports,
  fixturePushPreview,
  fixtureReview,
  fixtureTestConnection,
  fixtureUsers,
  fixtureWorkspaces,
} from "../../../components/Admin/AdminCrmImport/crmImport.fixtures";
import {
  ApiEnvelope,
  BusinessContext,
  CrmDefinition,
  Credentials,
  ImportListFilters,
  ImportRow,
  JobberConnectResult,
  LookupBusiness,
  LookupUser,
  LookupWorkspace,
  Pagination,
  PullStartPayload,
  PushEstimate,
  PushEstimatePayload,
  PushPreview,
  PushReviewPayload,
  PushStartPayload,
  ReviewResponse,
  TestConnectionPayload,
  TestConnectionResult,
  UserSearchParams,
} from "../../../components/Admin/AdminCrmImport/crmImport.types";

type CrmResponse<T> = { data: ApiEnvelope<T> };

const MOCK_LATENCY_MS = 350;

const mockResponse = <T>(data: T, pagination?: Pagination): Promise<CrmResponse<T>> =>
  new Promise((resolve) =>
    setTimeout(() => resolve({ data: { success: true, message: "", data, pagination } }), MOCK_LATENCY_MS)
  );

const mockPagination = (totalItems: number, page = 1, limit = 10): Pagination => ({
  currentPage: page,
  totalPages: Math.max(1, Math.ceil(totalItems / limit)),
  totalItems,
  itemsPerPage: limit,
});

const findMockImport = (id: string): ImportRow => fixtureImports.find((row) => row.id === id) || fixtureImports[0];

export const GetCrmsApi = (): Promise<CrmResponse<{ crms: CrmDefinition[] }>> =>
  USE_CRM_IMPORT_MOCK
    ? mockResponse({ crms: fixtureCrms })
    : AxiosInstance.get(apiRoutes.crmImportCrms);

export const SearchUsersApi = ({ keyword, page = 1, limit = 10 }: UserSearchParams): Promise<CrmResponse<{ users: LookupUser[] }>> => {
  if (USE_CRM_IMPORT_MOCK) {
    const term = keyword.trim().toLowerCase();
    const users = fixtureUsers.filter((u) => !term || u.email.toLowerCase().includes(term) || u.name.toLowerCase().includes(term));
    return mockResponse({ users }, mockPagination(users.length, page, limit));
  }
  return AxiosInstance.get(apiRoutes.crmImportLookupUsers, { params: { keyword, page, limit: Math.min(limit, 50) } });
};

export const GetUserWorkspacesApi = (userId: string): Promise<CrmResponse<{ workspaces: LookupWorkspace[] }>> =>
  USE_CRM_IMPORT_MOCK
    ? mockResponse({ workspaces: fixtureWorkspaces })
    : AxiosInstance.get(`${apiRoutes.crmImportLookupUsers}/${userId}/workspaces`);

export const GetWorkspaceBusinessesApi = (workspaceId: string): Promise<CrmResponse<{ businesses: LookupBusiness[] }>> =>
  USE_CRM_IMPORT_MOCK
    ? mockResponse({ businesses: fixtureBusinesses })
    : AxiosInstance.get(`${apiRoutes.crmImportLookupWorkspaces}/${workspaceId}/businesses`);

export const GetBusinessContextApi = (businessId: string): Promise<CrmResponse<BusinessContext>> =>
  USE_CRM_IMPORT_MOCK
    ? mockResponse(fixtureContext)
    : AxiosInstance.get(`${apiRoutes.crmImportLookupBusinessContext}/${businessId}/context`);

export const JobberConnectApi = (businessId: string): Promise<CrmResponse<JobberConnectResult>> =>
  USE_CRM_IMPORT_MOCK
    ? mockResponse({ connected: true, connectionId: "mock-connection", source: "integration" as const })
    : AxiosInstance.post(apiRoutes.crmImportJobberConnect, { businessId });

export const TestCrmConnectionApi =(payload: TestConnectionPayload): Promise<CrmResponse<TestConnectionResult>> =>
  USE_CRM_IMPORT_MOCK
    ? mockResponse({ ...fixtureTestConnection, tenantId: Object.values(payload.credentials)[0] || fixtureTestConnection.tenantId })
    : AxiosInstance.post(apiRoutes.crmImportPullTest, payload);

export const StartPullApi = (payload: PullStartPayload): Promise<CrmResponse<{ import: ImportRow }>> => {
  if (USE_CRM_IMPORT_MOCK) {
    const business = fixtureBusinesses.find((b) => b.id === payload.businessId);
    const row: ImportRow = {
      ...fixtureImports[0],
      id: `mock-pull-${Date.now()}`,
      seq: fixtureImports[0].seq + 1,
      status: "queued",
      stage: null,
      stageIndex: null,
      business: business ? { id: business.id, name: business.name } : fixtureImports[0].business,
      finishedAt: null,
      progress: { percent: 0 },
      actions: { canDownload: false, canRetry: false, canCancel: true, canDelete: false, canUseForPush: false },
    };
    return mockResponse({ import: row });
  }
  return AxiosInstance.post(apiRoutes.crmImportPullStart, payload);
};

export const ListImportsApi = (filters: ImportListFilters): Promise<CrmResponse<{ imports: ImportRow[]; summary: { running: number } }>> => {
  if (USE_CRM_IMPORT_MOCK) {
    const imports = fixtureImports.filter(
      (row) =>
        (!filters.rangePreset || row.pull?.rangePreset === filters.rangePreset) &&
        (!filters.status || row.status === filters.status) &&
        (!filters.businessId || row.business.id === filters.businessId)
    );
    const running = fixtureImports.filter((row) => row.status === "running" || row.status === "queued").length;
    return mockResponse({ imports, summary: { running } }, mockPagination(imports.length, filters.page, filters.limit));
  }
  const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== "" && value !== undefined));
  return AxiosInstance.get(apiRoutes.crmImportImports, { params });
};

export const GetImportApi = (id: string): Promise<CrmResponse<{ import: ImportRow }>> =>
  USE_CRM_IMPORT_MOCK
    ? mockResponse({ import: findMockImport(id) })
    : AxiosInstance.get(`${apiRoutes.crmImportImports}/${id}`);

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const fetchSheetBlob = async (url: string, params?: Record<string, unknown>): Promise<{ data: Blob }> => {
  try {
    return await AxiosInstance.get(url, { responseType: "blob", params });
  } catch (error: any) {
    const body = error?.response?.data;
    if (body instanceof Blob) {
      try {
        error.response.data = JSON.parse(await body.text());
      } catch {
        // body was not JSON, keep the original error
      }
    }
    throw error;
  }
};

export const DownloadSheetApi = async (id: string): Promise<{ data: Blob }> =>
  USE_CRM_IMPORT_MOCK
    ? { data: new Blob(["mock sheet"], { type: XLSX_MIME }) }
    : fetchSheetBlob(`${apiRoutes.crmImportImports}/${id}/sheet`);

export const CancelImportApi = (id: string): Promise<CrmResponse<{ import: ImportRow }>> => {
  if (USE_CRM_IMPORT_MOCK) {
    const row: ImportRow = {
      ...findMockImport(id),
      status: "canceled",
      actions: { canDownload: false, canRetry: false, canCancel: false, canDelete: true, canUseForPush: false },
    };
    return mockResponse({ import: row });
  }
  return AxiosInstance.post(`${apiRoutes.crmImportImports}/${id}/cancel`);
};

export const DeleteImportApi = (id: string): Promise<CrmResponse<{ id: string }>> => {
  if (USE_CRM_IMPORT_MOCK) return mockResponse({ id });
  return AxiosInstance.delete(`${apiRoutes.crmImportImports}/${id}`);
};

export const RetryImportApi = (id: string, credentials?: Credentials): Promise<CrmResponse<{ import: ImportRow }>> => {
  if (USE_CRM_IMPORT_MOCK) {
    const row: ImportRow = {
      ...findMockImport(id),
      status: "queued",
      error: null,
      progress: { percent: 0 },
      actions: { canDownload: false, canRetry: false, canCancel: true, canDelete: false, canUseForPush: false },
    };
    return mockResponse({ import: row });
  }
  return AxiosInstance.post(`${apiRoutes.crmImportImports}/${id}/retry`, credentials ? { credentials } : {});
};

export const PreviewPushApi = (file: File): Promise<CrmResponse<PushPreview>> => {
  if (USE_CRM_IMPORT_MOCK) {
    return mockResponse({ ...fixturePushPreview, push: { ...fixturePushPreview.push, fileName: file.name } });
  }
  const formData = new FormData();
  formData.append("file", file);
  return AxiosInstance.post(apiRoutes.crmImportPushPreview, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const EstimatePushApi = ({ pushId, selection }: PushEstimatePayload): Promise<CrmResponse<{ estimate: PushEstimate }>> =>
  USE_CRM_IMPORT_MOCK
    ? mockResponse({ estimate: fixtureEstimate })
    : AxiosInstance.post(`${apiRoutes.crmImportPush}/${pushId}/estimate`, selection);

export const ReviewPushApi = (payload: PushReviewPayload): Promise<CrmResponse<ReviewResponse>> =>
  USE_CRM_IMPORT_MOCK
    ? mockResponse(fixtureReview)
    : AxiosInstance.post(`${apiRoutes.crmImportPush}/${payload.pushId}/review`, payload.selection);

export const StartPushApi = ({ pushId, selection, reviewToken }: PushStartPayload): Promise<CrmResponse<{ import: ImportRow }>> => {
  if (USE_CRM_IMPORT_MOCK) {
    const row: ImportRow = {
      ...fixtureImports.find((r) => r.kind === "push") || fixtureImports[0],
      id: `mock-push-${pushId}`,
      status: "queued",
      actions: { canDownload: false, canRetry: false, canCancel: true, canDelete: false, canUseForPush: false },
    };
    return mockResponse({ import: row });
  }
  return AxiosInstance.post(`${apiRoutes.crmImportPush}/${pushId}/start`, { ...selection, reviewToken });
};
